import { Box, Circle, HoverCard, HStack, Icon, Text, VStack } from "@chakra-ui/react"
import { formatActivityKind } from "@hydrologiq/shared/utils/activities/activityStatus"
import React from "react"
import { IconType } from "react-icons"
import { DateTime } from "timezonecomplete"
import StatusBadge from "../StatusBadge"
import { mapStatusToBadge } from "../StatusBadge"
import { ActivityEventKind, ActivityKind, ActivityStatus } from "@hydrologiq/gql/graphql"
import { Activity, ActivityAttribute } from "./TimelineView"
import { FaTriangleExclamation } from "react-icons/fa6"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"

// Format date for display
const formatDate = (isoDate: string | undefined | null, format: string = "dd/MM/yyyy HH:mm"): string => {
  if (!isoDate) return ""
  try {
    return new DateTime(isoDate).format(format)
  } catch {
    return isoDate
  }
}

interface ActivityCellHoverProps {
  activity?: Activity
  color?: string
  backgroundColor?: string
  icon?: IconType
  children: React.ReactNode
  left?: number
  top?: number
  open?: boolean
}

const NotSet = (value: string | undefined | null) => {
  if (value) {
    return value
  }
  return (
    <>
      NOT SET <Icon mt={"-2px"} as={FaTriangleExclamation} />
    </>
  )
}

const quantityFromAttributes = (attributes: ActivityAttribute[] | undefined, kind: ActivityEventKind) => {
  return attributes?.find((attribute) => attribute.event_kind === kind && attribute.attribute === "quantity")?.value
}

const ActivityCellHover = ({
  activity,
  color = "white",
  backgroundColor = "primary",
  icon,
  children,
  left = 0,
  top = 0,
  open,
}: ActivityCellHoverProps) => {
  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.HEADINGS.QUANTITY]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.QUANTITY],
    [COPY_KEYS.HEADINGS.NOTE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NOTE],
  })

  return (
    <HoverCard.Root open={open} positioning={{ placement: "bottom-start" }}>
      <HoverCard.Trigger asChild>
        {/* Position the trigger at the mouse coordinates */}
        <Box
          position="fixed"
          left={`${left}px`}
          top={`${top}px`}
          width="1px"
          height="1px"
          visibility="hidden"
          pointerEvents="none"
        >
          {children}
        </Box>
      </HoverCard.Trigger>
      <HoverCard.Positioner zIndex={1000}>
        <HoverCard.Content color={color} backgroundColor={backgroundColor} p={3}>
          {/* <HoverCard.Arrow top="-5px !important">
            <HoverCard.ArrowTip
              background={`${backgroundColor} !important`}
              outline="none !important"
              border="none !important"
            />
          </HoverCard.Arrow> */}
          {activity && (
            <VStack gap={0} align="stretch" fontSize="xs">
              <HStack>
                <Circle
                  size="24px"
                  bg="card"
                  _hover={{ bg: "primary", color: "white" }}
                  color="primary"
                  fontWeight="bold"
                >
                  <Icon as={icon} />
                </Circle>
                <Text fontWeight="bold">{formatActivityKind(activity.kind)}</Text>
                <StatusBadge status={mapStatusToBadge(activity.status)} />
              </HStack>
              <Text>
                {formatDate(activity.start)} - {formatDate(activity.end)}
              </Text>
              {activity.kind === ActivityKind.Fulfilment && (
                <>
                  <Text>
                    {copy[COPY_KEYS.HEADINGS.ORDER]}: {NotSet(activity.order?.name)}
                  </Text>
                  {activity.customers?.map((customer) => (
                    <Text>
                      {copy[COPY_KEYS.HEADINGS.CUSTOMER]}: {customer.name}
                    </Text>
                  ))}
                  <Text>
                    {copy[COPY_KEYS.HEADINGS.SITE]}: {activity.site?.name}
                  </Text>
                  <HStack>
                    {[ActivityStatus.Started, ActivityStatus.Completed].includes(activity.status) ? (
                      <Text>
                        Delivered:{" "}
                        {quantityFromAttributes(activity?.attributes ?? undefined, ActivityEventKind.Started)}
                        kg
                      </Text>
                    ) : (
                      <Text>
                        Planned:{" "}
                        {NotSet(quantityFromAttributes(activity?.attributes ?? undefined, ActivityEventKind.Scheduled))}
                        kg
                      </Text>
                    )}
                    {activity.status === ActivityStatus.Completed && (
                      <Text>
                        Consumed:{" "}
                        {quantityFromAttributes(activity?.attributes ?? undefined, ActivityEventKind.Completed)}
                        kg
                      </Text>
                    )}
                  </HStack>
                </>
              )}
              {activity.kind === ActivityKind.Filling && (
                <>
                  <Text>
                    {copy[COPY_KEYS.HEADINGS.SITE]}: {NotSet(activity.site?.name)}
                  </Text>
                  <Text>
                    {copy[COPY_KEYS.HEADINGS.QUANTITY]}: {activity.quantity}
                  </Text>
                </>
              )}
              {activity.kind === ActivityKind.Movement && (
                <>
                  <Text>Source: {NotSet(activity.source_site?.name)}</Text>
                  <Text>Destination: {NotSet(activity.destination_site?.name)}</Text>
                  <HStack>
                    {activity.status !== ActivityStatus.Scheduled && (
                      <Text>
                        Collected:{" "}
                        {quantityFromAttributes(activity?.attributes ?? undefined, ActivityEventKind.Completed)}
                        kg
                      </Text>
                    )}
                    {[ActivityStatus.Completed].includes(activity.status) ? (
                      <Text>
                        Delivered:{" "}
                        {quantityFromAttributes(activity?.attributes ?? undefined, ActivityEventKind.Started)}
                        kg
                      </Text>
                    ) : (
                      <Text>
                        Planned:{" "}
                        {NotSet(quantityFromAttributes(activity?.attributes ?? undefined, ActivityEventKind.Scheduled))}
                        kg
                      </Text>
                    )}
                  </HStack>
                </>
              )}
              {[ActivityKind.Inspection, ActivityKind.Maintenance, ActivityKind.Hold].includes(activity.kind) && (
                <Text>
                  {copy[COPY_KEYS.HEADINGS.SITE]}: {NotSet(activity.site?.name)}
                </Text>
              )}
              {activity.note && (
                <Text>
                  {copy[COPY_KEYS.HEADINGS.NOTE]}: {activity.note}
                </Text>
              )}
            </VStack>
          )}
        </HoverCard.Content>
      </HoverCard.Positioner>
    </HoverCard.Root>
  )
}

export default ActivityCellHover
